import React, { useEffect, useMemo, useState } from 'react';
import TimeSelect, { BusinessHoursRange } from '../TimeSelect';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { colors, inputs, typography } from '../../theme/designSystem';
import SavingOverlay from '../SavingOverlay';
import {
  SeriesSlotRow,
  WeekdayPicker,
  buildSeriesRows,
  fromMinutes,
  generateSeriesDates,
  seriesLinkSx,
  seriesListSx,
  SeriesEdits,
  toMinutes,
  validateSeriesRows,
} from '../schedule/seriesShared';
import { ScheduleItem, ScheduleSlot } from '../../services/schedulesService';
import { ScheduleBlockItem } from '../../services/scheduleBlocksService';

// Tipos de bloqueio sugeridos; o campo aceita qualquer outro texto
const TITLE_SUGGESTIONS = ['Almoço', 'Reunião', 'Supervisão', 'Horário fechado', 'Curso / capacitação', 'Compromisso pessoal'];

const MAX_BLOCKS = 52;

export interface ScheduleBlockCreateData {
  professionalRegistrationId: string;
  title: string;
  notes: string | null;
  slots: ScheduleSlot[];
}

export interface ScheduleBlockUpdateData {
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  notes: string | null;
  applyToFollowing: boolean;
}

interface ScheduleBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** profissionais que o usuário pode bloquear (no escopo 'own', só o próprio) */
  professionals: { id: string; name: string }[];
  /** criação: profissional, data (yyyy-MM-dd) e horário sugeridos */
  defaults?: { professionalId?: string; date?: string; startTime?: string };
  /** presente = edição deste bloqueio */
  block?: ScheduleBlockItem | null;
  onCreate: (data: ScheduleBlockCreateData) => Promise<void>;
  onUpdate: (id: string, data: ScheduleBlockUpdateData) => Promise<void>;
  onDelete: (id: string, includeFollowing: boolean) => Promise<void>;
  /** explicação exibida quando não há profissional que o perfil possa bloquear */
  emptyReason?: string;
  /** edição vinda da lista de séries: já abre em "este e os seguintes" */
  defaultApplyToFollowing?: boolean;
  /** bloqueios existentes do profissional no período — bloqueio sobre bloqueio é recusado */
  loadBlocks?: (professionalId: string, from: string, to: string) => Promise<ScheduleBlockItem[]>;
  /** agendamentos ativos do profissional no período — bloquear em cima deles avisa, não impede */
  loadAppointments?: (professionalId: string, from: string, to: string) => Promise<ScheduleItem[]>;
  /** RN-115d: perfil sem "criar e editar bloqueio" abre o bloqueio só para ver */
  allowEdit?: boolean;
  /** RN-115d: perfil sem "excluir bloqueio" não vê o Excluir */
  allowDelete?: boolean;
  /** Horário de atendimento da clínica (RN-118o): recorta as listas de horário */
  businessHours?: BusinessHoursRange[] | null;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: inputs.default.height,
    fontSize: inputs.default.fontSize,
    '& fieldset': { borderColor: inputs.default.borderColor },
    '&:hover fieldset': { borderColor: inputs.default.hover.borderColor },
    '&.Mui-focused fieldset': {
      borderColor: inputs.default.focus.borderColor,
      boxShadow: inputs.default.focus.boxShadow,
    },
  },
} as const;

const labelProps = {
  shrink: true,
  sx: {
    fontSize: inputs.default.labelFontSize,
    color: inputs.default.labelColor,
    backgroundColor: inputs.default.labelBackground,
    padding: inputs.default.labelPadding,
    '&.Mui-focused': { color: inputs.default.focus.labelColor },
  },
} as const;

const sectionLabelSx = {
  fontSize: '0.9rem',
  fontWeight: typography.fontWeight.medium,
  color: colors.textPrimary,
  marginBottom: '0.25rem',
} as const;

const radioSx = { color: colors.primary, '&.Mui-checked': { color: colors.primary } } as const;

/**
 * Bloqueio de agenda (RN-118n): só uma marcação do profissional — reunião, almoço, horário
 * fechado. Sem paciente, equipe ou serviço; nunca cobrável; agendar por cima continua livre.
 * Recorrência com as mesmas peças do agendamento (dias da semana e lista editável).
 */
const ScheduleBlockModal: React.FC<ScheduleBlockModalProps> = ({
  isOpen, onClose, professionals, defaults, block, onCreate, onUpdate, onDelete, emptyReason,
  defaultApplyToFollowing = false,
  loadBlocks,
  loadAppointments,
  allowEdit = true,
  allowDelete = true,
  businessHours,
}) => {
  const isEdit = !!block;
  // Trava do passado: bloqueio encerrado é histórico, só leitura; e nada vai para antes de hoje
  const readOnly = isEdit && (!block?.canEdit || !!block?.isPast || !allowEdit);
  // Excluir independe de editar (RN-115d): aparece pelo alcance e pelo passado; a permissão própria
  // só desabilita (com o motivo), sem abrir a confirmação
  const showDelete = isEdit && !!block?.canEdit && !block?.isPast;
  const todayIso = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const [professionalId, setProfessionalId] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [count, setCount] = useState(4);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [edits, setEdits] = useState<SeriesEdits>({});
  const [applyToFollowing, setApplyToFollowing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado nasce a cada abertura: do bloqueio (edição) ou das sugestões da agenda (criação)
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setConfirmDelete(false);
    setApplyToFollowing(defaultApplyToFollowing && !!block && block.followingCount > 0);
    setRecurring(false);
    setRecurrenceType('weekly');
    setCount(4);
    setSkipped([]);
    setEdits({});
    if (block) {
      setProfessionalId(block.professionalRegistrationId);
      setTitle(block.title);
      setNotes(block.notes ?? '');
      setDate(block.dateTimeInitial.slice(0, 10));
      setStartTime(block.dateTimeInitial.slice(11, 16));
      setEndTime(block.dateTimeFinal.slice(11, 16));
    } else {
      const start = defaults?.startTime ?? '12:00';
      setProfessionalId(defaults?.professionalId ?? (professionals.length === 1 ? professionals[0].id : ''));
      setTitle('');
      setNotes('');
      setDate(defaults?.date ?? '');
      setStartTime(start);
      setEndTime(fromMinutes(toMinutes(start) + 60));
    }
    // professionals/defaults mudam de identidade a cada render do pai — só a abertura importa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, block]);

  // Dia da data inicial sempre entre os dias da semana e acompanhando a data
  const startDow = (() => {
    const d = date ? new Date(`${date}T00:00:00`) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getDay() : null;
  })();
  const previousStartDow = React.useRef<number | null>(null);
  useEffect(() => {
    if (startDow === null) return;
    setWeekdays(prev => {
      const kept = prev.filter(d => d !== previousStartDow.current && d !== startDow);
      return [...kept, startDow].sort((a, b) => a - b);
    });
    previousStartDow.current = startDow;
  }, [startDow, isOpen]);
  const toggleWeekday = (dow: number) =>
    setWeekdays(prev => (prev.includes(dow) ? prev.filter(d => d !== dow) : [...prev, dow].sort((a, b) => a - b)));

  // Trocar a regra descarta remoções e ajustes (pertencem à prévia que os gerou)
  useEffect(() => {
    setSkipped([]);
    setEdits({});
  }, [recurrenceType, date, recurring, weekdays, count]);

  const timeInvalid = !!startTime && !!endTime && endTime <= startTime;
  const duration = startTime && endTime && !timeInvalid ? toMinutes(endTime) - toMinutes(startTime) : 60;
  const weekBased = recurrenceType === 'weekly' || recurrenceType === 'biweekly';

  const seriesDates = useMemo(() => {
    if (isEdit || !recurring || !date) return [] as Date[];
    const first = new Date(`${date}T00:00:00`);
    if (Number.isNaN(first.getTime())) return [] as Date[];
    return generateSeriesDates({
      first,
      recurrenceType,
      count: Math.min(Math.max(count || 0, 2), MAX_BLOCKS),
      skipped,
      weekdays: weekBased && weekdays.length > 1 ? weekdays : [],
    });
  }, [isEdit, recurring, date, recurrenceType, count, skipped, weekdays, weekBased]);

  const rows = buildSeriesRows(seriesDates, edits, startTime, duration);
  const internalRowErrors = validateSeriesRows(rows, date, startTime, duration, 'ocorrência');

  // Bloqueios que já existem para o profissional nas datas em jogo (o servidor também recusa;
  // aqui é para a pessoa ver ANTES qual data bate). A API aceita janelas de até 62 dias.
  const [existingBlocks, setExistingBlocks] = useState<ScheduleBlockItem[]>([]);
  const datesInPlay = (recurring && !isEdit ? rows.map(r => r.date) : [date])
    .filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort();
  const overlapKey = isOpen && professionalId && datesInPlay.length > 0
    ? `${professionalId}|${datesInPlay[0]}|${datesInPlay[datesInPlay.length - 1]}`
    : '';
  useEffect(() => {
    if (!loadBlocks || !overlapKey) {
      setExistingBlocks([]);
      return;
    }
    const [pid, from, to] = overlapKey.split('|');
    const windows: [string, string][] = [];
    let cursor = new Date(`${from}T00:00:00`);
    const last = new Date(`${to}T00:00:00`);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    while (cursor <= last) {
      const end = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 59);
      windows.push([iso(cursor), iso(end < last ? end : last)]);
      cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    }
    let cancelled = false;
    Promise.all(windows.map(([f, t]) => loadBlocks(pid, f, t)))
      .then(parts => { if (!cancelled) setExistingBlocks(parts.flat()); })
      .catch(() => { if (!cancelled) setExistingBlocks([]); });
    return () => { cancelled = true; };
    // lambda nova a cada render do chamador — a chave do período decide a busca
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlapKey]);

  // Agendamentos do profissional nas mesmas datas: o bloqueio pode ser gravado por cima (pedido do
  // Hilton, 30/09), mas a pessoa decide sabendo quem já está marcado ali
  const [appointments, setAppointments] = useState<ScheduleItem[]>([]);
  useEffect(() => {
    if (!loadAppointments || !overlapKey || readOnly) {
      setAppointments([]);
      return;
    }
    const [pid, from, to] = overlapKey.split('|');
    let cancelled = false;
    loadAppointments(pid, from, to)
      .then(items => { if (!cancelled) setAppointments(items); })
      .catch(() => { if (!cancelled) setAppointments([]); });
    return () => { cancelled = true; };
    // lambda nova a cada render do chamador — a chave do período decide a busca
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlapKey, readOnly]);
  const appointmentsAt = (day: string, start: string, end: string): string | undefined => {
    const hits = appointments
      .filter(a => a.dateTimeInitial.slice(0, 10) === day
        && a.dateTimeInitial.slice(11, 16) < end && a.dateTimeFinal.slice(11, 16) > start)
      .sort((a, b) => a.dateTimeInitial.localeCompare(b.dateTimeInitial));
    if (hits.length === 0) return undefined;
    const names = hits.map(a => {
      const parts = (a.patientName ?? 'sem paciente').split(/\s+/).filter(Boolean);
      const short = parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]}` : parts[0];
      return `${a.dateTimeInitial.slice(11, 16)} ${short}`;
    });
    return `${hits.length === 1 ? 'agendamento' : `${hits.length} agendamentos`} (${names.join(', ')})`;
  };

  const existingOverlap = (day: string, start: string, end: string): string | undefined => {
    const hit = existingBlocks.find(b => b.id !== block?.id
      && b.dateTimeInitial.slice(0, 10) === day
      && b.dateTimeInitial.slice(11, 16) < end && b.dateTimeFinal.slice(11, 16) > start);
    return hit
      ? `Já existe bloqueio: ${hit.title} (${hit.dateTimeInitial.slice(11, 16)}–${hit.dateTimeFinal.slice(11, 16)}).`
      : undefined;
  };
  const rowErrors: Record<string, string> = { ...internalRowErrors };
  rows.forEach(r => {
    if (!rowErrors[r.key] && r.date && r.startTime) {
      const clash = existingOverlap(r.date, r.startTime, r.endTime);
      if (clash) rowErrors[r.key] = clash;
    }
  });
  // Só esta data (criação) ou edição: o conflito aparece sob a data
  const singleOverlap = (!recurring || isEdit) && date && startTime && endTime && !timeInvalid
    ? existingOverlap(date, startTime, endTime)
    : undefined;
  const adjustedCount = rows.filter(r => r.adjusted).length;
  const singleAppointments = (!recurring || isEdit) && date && startTime && endTime && !timeInvalid
    ? appointmentsAt(date, startTime, endTime)
    : undefined;
  const rowAppointments: Record<string, string> = {};
  if (recurring && !isEdit) {
    rows.forEach(r => {
      const hit = r.date && r.startTime ? appointmentsAt(r.date, r.startTime, r.endTime) : undefined;
      if (hit) rowAppointments[r.key] = hit;
    });
  }
  const rowsWithAppointments = Object.keys(rowAppointments).length;
  const overAppointments = !readOnly && (!!singleAppointments || rowsWithAppointments > 0);

  const titleMissing = !title.trim();
  const datePast = !!date && date < todayIso;
  const canSave = !saving && !readOnly && !titleMissing && !!professionalId && !!date && !!startTime && !!endTime
    && !timeInvalid && !datePast && !singleOverlap
    && (!recurring || isEdit || (rows.length > 0 && Object.keys(rowErrors).length === 0));

  const run = async (action: () => Promise<void>) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Não foi possível gravar o bloqueio. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!canSave) return;
    const cleanTitle = title.trim();
    const cleanNotes = notes.trim() ? notes.trim() : null;
    if (isEdit && block) {
      run(() => onUpdate(block.id, {
        date, startTime, endTime, title: cleanTitle, notes: cleanNotes,
        applyToFollowing: applyToFollowing && block.followingCount > 0,
      }));
      return;
    }
    const slots: ScheduleSlot[] = recurring
      ? rows.map(r => ({ date: r.date, startTime: r.startTime, endTime: r.endTime }))
      : [{ date, startTime, endTime }];
    run(() => onCreate({ professionalRegistrationId: professionalId, title: cleanTitle, notes: cleanNotes, slots }));
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '90vh', position: 'relative', overflow: 'hidden' } }}
    >
      <SavingOverlay open={saving} message="Gravando bloqueio..." />
      <DialogTitle sx={{
        backgroundColor: colors.primary,
        color: colors.white,
        padding: '1.25rem 1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.3rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}>
          {isEdit ? 'Bloqueio de agenda' : 'Bloquear agenda'}
        </Typography>
        <IconButton onClick={handleClose} disabled={saving} aria-label="Fechar"
          sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: { xs: '1rem', sm: '1.5rem' }, paddingTop: { xs: '1.25rem', sm: '1.5rem' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', paddingTop: '0.5rem' }}>
          {readOnly && block?.isPast ? (
            <Box role="status" sx={{
              backgroundColor: colors.backgroundSubtle,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: '8px',
              padding: '0.6rem 0.9rem',
              fontSize: '0.85rem',
              color: colors.textPrimary,
            }}>
              Este bloqueio já passou e fica como histórico: não pode ser alterado nem excluído.
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary, lineHeight: 1.45 }}>
              Marca o horário como indisponível na agenda. Não é atendimento, não gera cobrança e não
              impede agendar por cima.
            </Typography>
          )}

          <TextField
            select
            label="Profissional"
            value={professionalId}
            // Um profissional só: vem preenchido e travado; nenhum: a razão aparece embaixo
            disabled={isEdit || professionals.length <= 1}
            onChange={(e) => setProfessionalId(e.target.value)}
            error={!professionalId}
            helperText={!isEdit && professionals.length === 0 ? emptyReason : undefined}
            InputLabelProps={labelProps}
            sx={fieldSx}
          >
            {professionals.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>

          <Autocomplete
            freeSolo
            forcePopupIcon
            openOnFocus
            options={TITLE_SUGGESTIONS}
            disabled={readOnly}
            inputValue={title}
            onInputChange={(_, value) => setTitle(value.slice(0, 80))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de bloqueio"
                placeholder="Escolha na lista ou digite outro"
                error={titleMissing}
                InputLabelProps={labelProps}
                sx={fieldSx}
              />
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.5fr 1fr 1fr' }, gap: '1rem' }}>
            <TextField
              label="Data"
              type="date"
              value={date}
              disabled={readOnly}
              onChange={(e) => setDate(e.target.value)}
              error={!date || (datePast && !readOnly) || (!!singleOverlap && !readOnly)}
              helperText={readOnly ? undefined : datePast ? 'A data já passou.' : singleOverlap}
              inputProps={{ min: todayIso }}
              InputLabelProps={labelProps}
              sx={{ ...fieldSx, gridColumn: { xs: '1 / -1', sm: 'auto' } }}
            />
            <TimeSelect
              label="Início"
              value={startTime}
              businessHours={businessHours}
              disabled={readOnly}
              onChange={(next) => {
                // Mover o início desloca o fim, preservando a duração
                if (next && startTime && endTime && !timeInvalid) {
                  setEndTime(fromMinutes(toMinutes(next) + duration));
                }
                setStartTime(next);
              }}
              error={!startTime}
              InputLabelProps={labelProps}
              sx={fieldSx}
            />
            <TimeSelect
              label="Fim"
              value={endTime}
              after={startTime || undefined}
              businessHours={businessHours}
              disabled={readOnly}
              onChange={setEndTime}
              error={timeInvalid || !endTime}
              helperText={timeInvalid ? 'Deve ser depois do início.' : undefined}
              InputLabelProps={labelProps}
              sx={fieldSx}
            />
          </Box>

          {!isEdit && (
            <Box>
              <Typography sx={sectionLabelSx}>Repetição</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem 1rem' }}>
                <RadioGroup row value={recurring ? 'recurring' : 'unique'} onChange={(e) => setRecurring(e.target.value === 'recurring')}>
                  <FormControlLabel value="unique" control={<Radio sx={radioSx} />} label="Só esta data" />
                  <FormControlLabel value="recurring" control={<Radio sx={radioSx} />} label="Repetir" />
                </RadioGroup>
                {recurring && (
                  <TextField
                    type="number"
                    label="Ocorrências"
                    value={count}
                    onChange={(e) => setCount(Math.min(Number(e.target.value) || 0, MAX_BLOCKS))}
                    inputProps={{ min: 2, max: MAX_BLOCKS }}
                    InputLabelProps={labelProps}
                    sx={{ ...fieldSx, width: 130 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {!isEdit && recurring && (
            <>
              <RadioGroup
                value={recurrenceType}
                onChange={(e) => setRecurrenceType(e.target.value)}
                sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, auto)' }, justifyContent: 'start', columnGap: '1rem' }}
              >
                <FormControlLabel value="daily" control={<Radio sx={radioSx} />} label="Diário (seg a sex)" />
                <FormControlLabel value="weekly" control={<Radio sx={radioSx} />} label="Semanal" />
                <FormControlLabel value="biweekly" control={<Radio sx={radioSx} />} label="Quinzenal" />
                <FormControlLabel value="monthly" control={<Radio sx={radioSx} />} label="Mensal" />
              </RadioGroup>

              {weekBased && startDow !== null && (
                <WeekdayPicker value={weekdays} anchor={startDow} onToggle={toggleWeekday} />
              )}

              {rows.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: '0.75rem', marginBottom: '0.25rem' }}>
                    <Typography sx={sectionLabelSx}>{`Ocorrências (${rows.length})`}</Typography>
                    {skipped.length > 0 && (
                      <Button variant="text" size="small" onClick={() => setSkipped([])} sx={seriesLinkSx}>
                        {`restaurar removidas (${skipped.length})`}
                      </Button>
                    )}
                    {adjustedCount > 0 && (
                      <Button variant="text" size="small" onClick={() => setEdits({})} sx={seriesLinkSx}>
                        {`desfazer ajustes (${adjustedCount})`}
                      </Button>
                    )}
                  </Box>
                  <Box sx={seriesListSx}>
                    {rows.map(row => (
                      <SeriesSlotRow
                        key={row.key}
                        number={row.index + 1}
                        date={row.date}
                        startTime={row.startTime}
                        endTime={row.endTime}
                        minDate={date || undefined}
                        editable={row.index > 0}
                        lockedReason={row.index === 0 ? 'Primeira ocorrência: segue a data e o horário acima.' : undefined}
                        adjusted={row.adjusted}
                        error={rowErrors[row.key]}
                        conflict={rowAppointments[row.key]}
                        onDateChange={(value) => setEdits(prev => ({ ...prev, [row.key]: { ...prev[row.key], date: value } }))}
                        onTimeChange={(value) => setEdits(prev => ({ ...prev, [row.key]: { ...prev[row.key], startTime: value } }))}
                        onRemove={row.index > 0
                          ? () => {
                            setEdits(prev => {
                              const next = { ...prev };
                              delete next[row.key];
                              return next;
                            });
                            setSkipped(prev => [...prev, row.key]);
                          }
                          : undefined}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}

          <TextField
            label="Observações"
            disabled={readOnly}
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            InputLabelProps={labelProps}
          />

          {isEdit && block && !readOnly && block.followingCount > 0 && (
            <Box>
              <Typography sx={sectionLabelSx}>Aplicar a alteração em</Typography>
              <RadioGroup value={applyToFollowing ? 'following' : 'single'} onChange={(e) => setApplyToFollowing(e.target.value === 'following')}>
                <FormControlLabel value="single" control={<Radio sx={radioSx} />} label="Só este bloqueio" />
                <FormControlLabel value="following" control={<Radio sx={radioSx} />}
                  label={`Este e os ${block.followingCount} seguintes (título, observações e horário; as datas não mudam)`} />
              </RadioGroup>
            </Box>
          )}

          {overAppointments && (
            <Box
              role="status"
              sx={{
                backgroundColor: colors.warningSoft,
                border: '1px solid #ffe69c',
                color: '#664d03',
                borderRadius: '8px',
                padding: '0.6rem 0.9rem',
                fontSize: '0.85rem',
              }}
            >
              <strong>Há paciente agendado neste horário:</strong>{' '}
              {singleAppointments ?? `${rowsWithAppointments} ${rowsWithAppointments === 1 ? 'ocorrência coincide' : 'ocorrências coincidem'} com agendamento (veja a lista)`}.
              {' '}O bloqueio pode ser gravado assim, e os agendamentos continuam valendo; remarque ou cancele pela agenda se for o caso.
            </Box>
          )}

          {error && (
            <Box role="alert" sx={{
              backgroundColor: colors.errorSoft,
              border: '1px solid #f5c2c7',
              color: '#842029',
              borderRadius: '8px',
              padding: '0.6rem 0.9rem',
              fontSize: '0.85rem',
            }}>
              {error}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{
        padding: { xs: '1rem', sm: '1.25rem 1.5rem' },
        borderTop: `1px solid ${colors.backgroundAlt}`,
        backgroundColor: colors.background,
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          {showDelete && !confirmDelete && (
            <span title={allowDelete ? undefined : 'Seu perfil não exclui bloqueios'}>
              <Button variant="text" onClick={() => setConfirmDelete(true)} disabled={saving || !allowDelete}
                sx={{ textTransform: 'none', color: colors.error }}>
                Excluir bloqueio
              </Button>
            </span>
          )}
          {isEdit && block && confirmDelete && (
            <>
              <Typography sx={{ fontSize: '0.85rem', color: colors.textPrimary }}>Excluir:</Typography>
              <Button variant="outlined" size="small" disabled={saving}
                onClick={() => run(() => onDelete(block.id, false))}
                sx={{ textTransform: 'none', borderColor: colors.error, color: colors.error }}>
                Só este
              </Button>
              {block.followingCount > 0 && (
                <Button variant="outlined" size="small" disabled={saving}
                  onClick={() => run(() => onDelete(block.id, true))}
                  sx={{ textTransform: 'none', borderColor: colors.error, color: colors.error }}>
                  {`Este e os ${block.followingCount} seguintes`}
                </Button>
              )}
              <Button variant="text" size="small" disabled={saving} onClick={() => setConfirmDelete(false)}
                sx={{ textTransform: 'none', color: colors.textSecondary }}>
                Manter
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
          <Button variant="text" onClick={handleClose} disabled={saving} sx={{ textTransform: 'none', color: colors.textSecondary }}>
            Cancelar
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              disableElevation
              disabled={!canSave}
              onClick={handleSave}
              sx={{
                textTransform: 'none',
                padding: '0.6rem 1.4rem',
                borderRadius: '6px',
                backgroundColor: colors.primary,
                fontWeight: typography.fontWeight.medium,
                '&:hover': { backgroundColor: colors.primaryHover },
                '&.Mui-disabled': { backgroundColor: '#a8dde3', color: colors.white },
              }}
            >
              {isEdit
                ? (overAppointments ? 'Salvar mesmo assim' : 'Salvar')
                : recurring && rows.length > 1
                  ? `Bloquear ${rows.length} horários${overAppointments ? ' mesmo assim' : ''}`
                  : (overAppointments ? 'Bloquear mesmo assim' : 'Bloquear')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleBlockModal;
